import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from './supabaseClient.js';

const STATUS_COLORS = {
  draft: 'bg-gray-500',
  active: 'bg-green-600',
  completed: 'bg-blue-500',
  cancelled: 'bg-red-500',
};

const COMPLETED_PAGE_SIZE = 30;

// Get Sunday of the week for a given date
const getWeekSunday = (dateStr) => {
  const d = new Date(dateStr + 'T00:00:00');
  const sunday = new Date(d);
  sunday.setDate(d.getDate() - d.getDay()); // back up to Sunday (Sun=0 … Sat=6)
  return sunday;
};

const formatWeekRange = (sunday) => {
  const saturday = new Date(sunday);
  saturday.setDate(sunday.getDate() + 6);
  const fmt = (d) => d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
  return `${fmt(sunday)} – ${fmt(saturday)}`;
};

const formatWeekKey = (sunday) => sunday.toISOString().split('T')[0];

const computeStatsFromItems = (items) => {
  const stats = {};
  for (const item of items) {
    if (!stats[item.route_id]) stats[item.route_id] = { jobCount: 0, totalHours: 0, totalValue: 0 };
    const s = stats[item.route_id];
    if (item.row_type === 'JOB') {
      s.jobCount++;
      s.totalHours += parseFloat(item.crm_job?.estimated_hours) || 0;
      s.totalValue += parseFloat(item.crm_job?.value) || 0;
    }
    if (item.row_type === 'TRAVEL' || item.row_type === 'TRANSIT') {
      // Transit contributes hours only (no money, no job count)
      s.totalHours += parseFloat(item.travel_hours) || 0;
    }
  }
  return stats;
};

const RoutesListPage = () => {
  const [upcomingRoutes, setUpcomingRoutes] = useState([]);
  const [completedRoutes, setCompletedRoutes] = useState([]);
  const [routeStats, setRouteStats] = useState({}); // { routeId: { jobCount, totalHours, totalValue } }
  const [applicators, setApplicators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('upcoming');
  const [showCreate, setShowCreate] = useState(false);
  const [newRoute, setNewRoute] = useState({ route_name: '', corridor: '', start_date: '', notes: '' });
  const [creating, setCreating] = useState(false);

  const [completedHasMore, setCompletedHasMore] = useState(true);
  const [loadingMoreCompleted, setLoadingMoreCompleted] = useState(false);
  const [statsLoading, setStatsLoading] = useState({ upcoming: false, completed: false });

  // Track which tabs have been loaded so we don't refetch on every tab switch
  const loadedTabs = useRef({ upcoming: false, completed: false });

  const corridors = [
    'Okanagan South',
    'Okanagan North',
    'Nicola/Merritt',
    'Kootenays',
    'Cariboo/North',
    'Lower Mainland',
  ];

  const fetchStatsFor = useCallback(async (routeIds) => {
    if (routeIds.length === 0) return {};
    const { data, error } = await supabase
      .from('route_items')
      .select('route_id, row_type, travel_hours, crm_job:crm_jobs(value, estimated_hours)')
      .in('route_id', routeIds);
    if (error) {
      console.error('Error fetching route items for stats:', error);
      return {};
    }
    return computeStatsFromItems(data || []);
  }, []);

  // Kick off the stats query without blocking the caller — routes render
  // immediately and stat columns/totals fill in as the query resolves.
  const loadStatsInBackground = useCallback((routeIds, tabKey) => {
    if (routeIds.length === 0) return;
    setStatsLoading((prev) => ({ ...prev, [tabKey]: true }));
    fetchStatsFor(routeIds)
      .then((stats) => setRouteStats((prev) => ({ ...prev, ...stats })))
      .finally(() => setStatsLoading((prev) => ({ ...prev, [tabKey]: false })));
  }, [fetchStatsFor]);

  const fetchUpcoming = useCallback(async () => {
    const { data, error } = await supabase
      .from('routes')
      .select('*')
      .in('status', ['draft', 'active'])
      .order('start_date', { ascending: true, nullsFirst: false });
    if (error) {
      console.error('Error fetching upcoming routes:', error);
      return;
    }
    const routes = data || [];
    setUpcomingRoutes(routes);
    loadedTabs.current.upcoming = true;
    loadStatsInBackground(routes.map((r) => r.id), 'upcoming');
  }, [loadStatsInBackground]);

  const fetchCompleted = useCallback(async ({ offset = 0, append = false } = {}) => {
    const { data, error } = await supabase
      .from('routes')
      .select('*')
      .eq('status', 'completed')
      .order('start_date', { ascending: false })
      .range(offset, offset + COMPLETED_PAGE_SIZE - 1);
    if (error) {
      console.error('Error fetching completed routes:', error);
      return;
    }
    const newRoutes = data || [];
    setCompletedRoutes((prev) => (append ? [...prev, ...newRoutes] : newRoutes));
    setCompletedHasMore(newRoutes.length === COMPLETED_PAGE_SIZE);
    loadedTabs.current.completed = true;
    loadStatsInBackground(newRoutes.map((r) => r.id), 'completed');
  }, [loadStatsInBackground]);

  const fetchApplicators = useCallback(async () => {
    const { data, error } = await supabase
      .from('applicators')
      .select('id, name')
      .not('user_id', 'is', null);
    if (error) {
      console.error('Error fetching applicators:', error);
      return;
    }
    setApplicators(data || []);
  }, []);

  useEffect(() => {
    (async () => {
      await Promise.all([fetchUpcoming(), fetchApplicators()]);
      setLoading(false);
    })();

    const channel = supabase
      .channel('routes-list')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'routes' }, () => {
        if (loadedTabs.current.upcoming) fetchUpcoming();
        if (loadedTabs.current.completed) fetchCompleted({ offset: 0, append: false });
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [fetchUpcoming, fetchCompleted, fetchApplicators]);

  useEffect(() => {
    if (tab === 'completed' && !loadedTabs.current.completed) {
      fetchCompleted({ offset: 0, append: false });
    }
  }, [tab, fetchCompleted]);

  const handleLoadMoreCompleted = async () => {
    setLoadingMoreCompleted(true);
    await fetchCompleted({ offset: completedRoutes.length, append: true });
    setLoadingMoreCompleted(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);

    const { error } = await supabase.from('routes').insert([{
      route_name: newRoute.route_name,
      corridor: newRoute.corridor || null,
      start_date: newRoute.start_date || null,
      notes: newRoute.notes || null,
    }]);

    if (error) {
      console.error('Error creating route:', error);
    } else {
      setNewRoute({ route_name: '', corridor: '', start_date: '', notes: '' });
      setShowCreate(false);
      fetchUpcoming();
    }
    setCreating(false);
  };

  const upcomingTotal = upcomingRoutes.reduce((sum, r) => sum + (routeStats[r.id]?.totalValue || 0), 0);
  const completedTotal = completedRoutes.reduce((sum, r) => sum + (routeStats[r.id]?.totalValue || 0), 0);

  // Group upcoming routes by week (Sun-Sat)
  const weekGroups = {};
  for (const route of upcomingRoutes) {
    if (!route.start_date) {
      const key = 'unscheduled';
      if (!weekGroups[key]) weekGroups[key] = { label: 'Unscheduled', routes: [], sortKey: '9999' };
      weekGroups[key].routes.push(route);
      continue;
    }
    const sunday = getWeekSunday(route.start_date);
    const key = formatWeekKey(sunday);
    if (!weekGroups[key]) {
      weekGroups[key] = { label: `Week of ${formatWeekRange(sunday)}`, routes: [], sortKey: key };
    }
    weekGroups[key].routes.push(route);
  }
  const sortedWeekKeys = Object.keys(weekGroups).sort((a, b) =>
    weekGroups[a].sortKey.localeCompare(weekGroups[b].sortKey)
  );

  const getApplicatorName = (id) => applicators.find((a) => a.id === id)?.name;

  const inputClass = 'w-full px-3 py-2 bg-gray-900 border border-gray-500 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-green-600';

  const renderRouteCard = (route) => {
    const stats = routeStats[route.id];
    const applicatorName = getApplicatorName(route.applicator_id);

    return (
      <Link
        key={route.id}
        to={`/portal/routes/${route.id}`}
        className="block bg-gray-700 rounded-lg p-4 hover:bg-gray-700/80 transition-colors"
      >
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-white truncate">{route.route_name}</h2>
            <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-gray-300">
              {route.corridor && <span>{route.corridor}</span>}
              {route.start_date && (
                <span>
                  {new Date(route.start_date + 'T00:00:00').toLocaleDateString('en-CA', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                  })}
                </span>
              )}
              {applicatorName && (
                <span className="text-gray-400">{applicatorName}</span>
              )}
              {stats ? (
                <>
                  <span className="text-gray-400">{stats.jobCount} jobs</span>
                  <span className="text-gray-400">{stats.totalHours.toFixed(1)}h</span>
                  {stats.totalValue > 0 && (
                    <span className="text-green-400 font-medium">${stats.totalValue.toLocaleString()}</span>
                  )}
                </>
              ) : (
                <span className="inline-block animate-spin h-3 w-3 border-2 border-gray-500 border-t-gray-300 rounded-full" title="Loading stats..."></span>
              )}
            </div>
          </div>
          <span
            className={`px-2 py-1 text-xs font-medium text-white rounded-full shrink-0 ${
              STATUS_COLORS[route.status] || STATUS_COLORS.draft
            }`}
          >
            {route.status}
          </span>
        </div>
        {route.notes && (
          <p className="text-sm text-gray-400 mt-2 truncate">{route.notes}</p>
        )}
      </Link>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center gap-3 text-gray-300 mt-8">
        <div className="animate-spin h-5 w-5 border-2 border-gray-500 border-t-green-600 rounded-full"></div>
        Loading routes...
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">Routes</h1>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="px-4 py-2 bg-green-700 text-white text-sm font-medium rounded-md hover:bg-green-600 transition-colors"
        >
          {showCreate ? 'Cancel' : '+ New Route'}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex justify-center mb-6">
        <div className="inline-flex bg-gray-700 rounded-lg p-1">
          <button
            onClick={() => setTab('upcoming')}
            className={`px-5 py-2 text-sm font-medium rounded-md transition-colors inline-flex items-center gap-2 ${
              tab === 'upcoming'
                ? 'bg-green-700 text-white'
                : 'text-gray-300 hover:text-white'
            }`}
          >
            <span>Upcoming ({upcomingRoutes.length})</span>
            {statsLoading.upcoming ? (
              <span className="inline-block animate-spin h-3 w-3 border-2 border-current border-t-transparent rounded-full" title="Loading total..."></span>
            ) : upcomingTotal > 0 ? (
              <span>— ${upcomingTotal.toLocaleString()}</span>
            ) : null}
          </button>
          <button
            onClick={() => setTab('completed')}
            className={`px-5 py-2 text-sm font-medium rounded-md transition-colors inline-flex items-center gap-2 ${
              tab === 'completed'
                ? 'bg-green-700 text-white'
                : 'text-gray-300 hover:text-white'
            }`}
          >
            <span>
              Completed
              {loadedTabs.current.completed && ` (${completedRoutes.length}${completedHasMore ? '+' : ''})`}
            </span>
            {statsLoading.completed ? (
              <span className="inline-block animate-spin h-3 w-3 border-2 border-current border-t-transparent rounded-full" title="Loading total..."></span>
            ) : completedTotal > 0 ? (
              <span>— ${completedTotal.toLocaleString()}</span>
            ) : null}
          </button>
        </div>
      </div>

      {/* Create form */}
      {showCreate && (
        <form onSubmit={handleCreate} className="bg-gray-700 rounded-lg p-4 mb-6 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">Route Name *</label>
              <input
                type="text"
                value={newRoute.route_name}
                onChange={(e) => setNewRoute({ ...newRoute, route_name: e.target.value })}
                required
                className={inputClass}
                placeholder="e.g. Northern BC Route"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">Corridor</label>
              <select
                value={newRoute.corridor}
                onChange={(e) => setNewRoute({ ...newRoute, corridor: e.target.value })}
                className={inputClass}
              >
                <option value="">Select corridor...</option>
                {corridors.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">Start Date</label>
              <input
                type="date"
                value={newRoute.start_date}
                onChange={(e) => setNewRoute({ ...newRoute, start_date: e.target.value })}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">Notes</label>
              <input
                type="text"
                value={newRoute.notes}
                onChange={(e) => setNewRoute({ ...newRoute, notes: e.target.value })}
                className={inputClass}
                placeholder="Optional notes"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={creating}
            className="px-4 py-2 bg-green-700 text-white text-sm font-medium rounded-md hover:bg-green-600 disabled:opacity-50 transition-colors"
          >
            {creating ? 'Creating...' : 'Create Route'}
          </button>
        </form>
      )}

      {/* Upcoming tab: grouped by week */}
      {tab === 'upcoming' && (
        upcomingRoutes.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-400 text-lg">No upcoming routes.</p>
            <p className="text-gray-400 text-sm mt-1">Create a route to get started.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {sortedWeekKeys.map((key) => {
              const group = weekGroups[key];
              return (
                <div key={key}>
                  <h2 className="text-sm font-semibold text-gray-300 mb-2 px-1">{group.label}</h2>
                  <div className="space-y-3">
                    {group.routes.map(renderRouteCard)}
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {/* Completed tab: flat list sorted by date desc, paginated */}
      {tab === 'completed' && (
        !loadedTabs.current.completed ? (
          <div className="flex items-center gap-3 text-gray-300 mt-4">
            <div className="animate-spin h-5 w-5 border-2 border-gray-500 border-t-green-600 rounded-full"></div>
            Loading completed routes...
          </div>
        ) : completedRoutes.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-400 text-lg">No completed routes.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {completedRoutes.map(renderRouteCard)}
            {completedHasMore && (
              <button
                onClick={handleLoadMoreCompleted}
                disabled={loadingMoreCompleted}
                className="w-full px-4 py-2 bg-gray-700 text-gray-300 text-sm font-medium rounded-md hover:bg-gray-700/80 disabled:opacity-50 transition-colors"
              >
                {loadingMoreCompleted ? 'Loading...' : 'Load more'}
              </button>
            )}
          </div>
        )
      )}
    </div>
  );
};

export default RoutesListPage;
